import { useEffect, useRef, useState } from 'react';

import { getRandomClientNumber } from '@src/Common/Constants/getRandom';
import useWindowDimensions from '@src/Common/Hooks/useWindowDimensions';
import { IItemTimeLine } from '@src/Domain/DUMEvents/DUMEvents.type';

import { positionAnimation } from './TimeLine.animation';
import { getPositionAnimation, handleFindMytic } from './TimeLine.const';
import { TimeLineProps } from './TimeLine.type';

export const useTimeLine = ({
  starYear,
  endYear,
  dataItems,
  isDetectedAction,
  onActiveTimeline
}: TimeLineProps) => {
  const { width } = useWindowDimensions();

  const stepsRef = useRef<HTMLDivElement>(null);
  const [yearRange, setYearRange] = useState<Array<number>>([2013, 2023]);
  const [allItems, setAllItems] = useState<Array<IItemTimeLine>>([]);
  const [inputValue, setInputValue] = useState<number>(1);
  const [inputMonth, setInputMonth] = useState<number>(0);

  useEffect(() => {
    setYearRange([starYear, endYear]);
    setAllItems(dataItems);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animation = (indexItem: number) => {
    if (stepsRef.current) {
      const prevCard = stepsRef.current;
      const position = getPositionAnimation({
        width,
        indexItem,
        allItems
      });
      positionAnimation(prevCard, position);
    }
  };

  const handleChange = (newValue: number | null) => {
    if (newValue !== null) {
      setInputMonth(newValue - 1);
      setInputValue(newValue ?? 0);
      animation(newValue - 1);
      onActiveTimeline(dataItems[newValue - 1]);
    }
  };

  const handleAfterChange = (newValue: number | null) => {
    if (newValue !== null) {
      onActiveTimeline(dataItems[newValue - 1]);
    }
  };

  const handleClickIcon = (
    type: 'prev' | 'next' | 'firstItem' | 'lastItem'
  ) => {
    let totalMonth;
    let totalValue;
    switch (type) {
      case 'next':
        totalMonth = inputMonth + 1;
        totalValue = inputValue + 1;
        break;
      case 'prev':
        totalMonth = inputMonth - 1;
        totalValue = inputValue - 1;
        break;
      case 'lastItem':
        totalMonth = allItems.length - 1;
        totalValue = allItems.length;
        break;
      default:
        totalMonth = 0;
        totalValue = 1;
        break;
    }

    if (
      totalMonth !== undefined &&
      totalMonth >= 0 &&
      totalMonth <= allItems.length
    ) {
      setInputMonth(totalMonth);
      setInputValue(totalValue);
      animation(totalValue);
      onActiveTimeline(dataItems[totalMonth]);
    }
  };

  useEffect(() => {
    if (allItems.length === 0) {
      return;
    }
    // check if has year & month & dum_id
    const query = new URLSearchParams(window.location.search);
    const year = query.get('year');
    const month = query.get('month')?.padStart(2, '0');
    const dum_id = query.get('dum_id');
    if (year && month && dum_id && allItems.length > 0) {
      const selectedIndex = dataItems.findIndex(
        (dataItem) =>
          dataItem.month === Number(month) && dataItem.year === Number(year)
      );
      if (selectedIndex > -1) {
        setTimeout(() => {
          handleChange(selectedIndex + 1);
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems]);

  const handleMythicEvent = (type: 'earlier' | 'latest') => {
    const direction = type === 'earlier' ? -1 : 1;
    let foundIndex = -1;

    for (
      let i = inputMonth + direction;
      i >= 0 && i < allItems.length;
      i += direction
    ) {
      const e = allItems[i];
      if ('isMythicEvent' in e && e.isMythicEvent) {
        foundIndex = i;
        break;
      }
    }

    if (foundIndex >= 0) {
      setInputMonth(foundIndex);
      setInputValue(foundIndex);
      animation(foundIndex);
      onActiveTimeline(dataItems[foundIndex]);
    }
  };

  useEffect(() => {
    if (isDetectedAction) return;

    const time = Number(process.env.REACT_APP_ANIMATION_TIME);
    const data = handleFindMytic(allItems);
    const randomNumber = getRandomClientNumber(data.length);

    const interval = setInterval(() => {
      if (randomNumber >= 0) handleChange(data[randomNumber]);
    }, time);

    return () => {
      clearInterval(interval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChange, isDetectedAction]);

  return {
    stepsRef,
    yearRange,
    allItems,
    inputValue,
    inputMonth,
    handleChange,
    handleClickIcon,
    handleMythicEvent,
    handleAfterChange
  };
};
